// POS thermal receipt printer (80mm or 58mm).
// Uses Blob URL to render an isolated HTML doc and triggers print automatically.
import { API_BASE } from "@/lib/api";
import { selectedServiceLabel } from "@/lib/packageLines";

const resolveImg = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return API_BASE.replace(/\/api$/, "") + u;
};

const fmt = (n) => Number(n || 0).toFixed(2);

function escape(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatPaymentLine(invoice, isAR) {
  const splits = invoice?.payment_splits;
  if (Array.isArray(splits) && splits.length) {
    const parts = splits.map((s) => `${s.payment_method || ""} ${fmt(s.amount)}`);
    return isAR ? `مقسّم: ${parts.join(" + ")}` : `Split: ${parts.join(" + ")}`;
  }
  return invoice.payment_method || "";
}

export function printPosReceipt(invoice, fullTemplate, lang = "ar") {
  const tpl = (fullTemplate && fullTemplate.pos) || {};
  const bizPhone = String(fullTemplate?.phone || tpl.phone || "").trim();
  const isAR = (tpl.language || lang) === "ar";
  const dir = isAR ? "rtl" : "ltr";
  const t = (en, ar) => (isAR ? ar : en);
  const widthMM = Number(tpl.paper_width_mm || 80);
  const innerWidthMM = widthMM - 6; // 3mm padding each side

  const dateStr = (() => {
    try { return new Date(invoice.created_at).toLocaleString(isAR ? "ar-EG" : "en-US"); }
    catch { return invoice.created_at || ""; }
  })();

  const rows = (invoice.items || []).map((it) => {
    const lineSubtotal = (it.unit_price || 0) * (it.qty || 1);
    const lineDisc = Number(it.line_discount_amount || 0);
    const lineTotal = it.line_total != null ? Number(it.line_total) : (lineSubtotal - lineDisc);
    const itemName = (lang === "ar" ? (it.name_ar || it.name) : (it.name_en || it.name)) || it.name || "";
    const subs = (it.selected_services || []).map((sel) => `
      <tr class="item-row">
        <td class="item-name" style="padding-inline-start:8px;font-size:10px;color:#444;">• ${escape(selectedServiceLabel(sel, lang))}</td>
        <td class="qty"></td>
        <td class="amt"></td>
      </tr>
    `).join("");
    return `
      <tr class="item-row">
        <td class="item-name">${escape(itemName)}</td>
        <td class="qty">${it.qty}</td>
        <td class="amt">${fmt(lineTotal)}</td>
      </tr>
      ${it.qty > 1 ? `<tr><td colspan="3" class="unit-line">${fmt(it.unit_price)} × ${it.qty}</td></tr>` : ""}
      ${lineDisc > 0 ? `<tr><td colspan="3" class="unit-line" style="color:#b91c1c">${escape(it.discount_reason_label || (isAR ? "خصم" : "Discount"))}: -${fmt(lineDisc)}</td></tr>` : ""}
      ${subs}
    `;
  }).join("");

  // QR payload: invoice_number + total + date — minimal e-receipt payload
  const qrPayload = encodeURIComponent(JSON.stringify({
    n: invoice.invoice_number || "",
    t: invoice.total,
    d: invoice.created_at,
  }));
  const qrUrl = tpl.show_qr ? `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${qrPayload}` : "";

  const html = `
<!DOCTYPE html>
<html lang="${isAR ? "ar" : "en"}" dir="${dir}">
<head>
<meta charset="UTF-8" />
<title>${escape(invoice.invoice_number || "Receipt")}</title>
<style>
  @page { size: ${widthMM}mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    width: ${widthMM}mm;
    font-family: ${isAR ? "'IBM Plex Sans Arabic', 'Tahoma', sans-serif" : "'Inter', 'Courier New', monospace"};
    color: #000; background: #fff;
    font-size: 11px;
    line-height: 1.35;
  }
  .receipt { width: ${innerWidthMM}mm; padding: 3mm; margin: 0 auto; }
  .center { text-align: center; }
  .biz-logo { display: block; margin: 0 auto 4px auto; max-width: 50mm; max-height: 18mm; }
  .biz-name { font-size: 14px; font-weight: 700; margin: 1mm 0; }
  .biz-meta { font-size: 10px; color: #222; line-height: 1.4; }
  .biz-meta div { margin: 0.5mm 0; }
  .divider { border-top: 1px dashed #000; margin: 2mm 0; }
  .double-divider { border-top: 1.5px solid #000; margin: 2mm 0; }
  .doc-title { font-weight: 700; font-size: 13px; margin: 1mm 0; }
  .info-line { display: flex; justify-content: space-between; font-size: 10.5px; margin: 0.6mm 0; }
  .info-line .lbl { color: #444; }
  .info-line .val { font-weight: 600; }
  table { width: 100%; border-collapse: collapse; }
  thead th { font-size: 10px; padding: 1.5mm 0.5mm; border-top: 1px solid #000; border-bottom: 1px solid #000; font-weight: 700; }
  thead th.qty, thead th.amt { width: 18%; text-align: ${isAR ? "left" : "right"}; }
  thead th.item-h { text-align: ${isAR ? "right" : "left"}; }
  td { padding: 1mm 0.5mm; vertical-align: top; }
  td.item-name { font-size: 11px; }
  td.qty, td.amt { text-align: ${isAR ? "left" : "right"}; font-variant-numeric: tabular-nums; font-size: 11px; }
  td.unit-line { font-size: 9px; color: #555; padding-bottom: 1.2mm; padding-${isAR ? "right" : "left"}: 1mm; }
  .totals .info-line { font-size: 11px; }
  .totals .grand { font-size: 14px; font-weight: 800; padding: 1mm 0; border-top: 1.5px solid #000; border-bottom: 1.5px solid #000; margin-top: 1mm; }
  .footer { text-align: center; margin-top: 2mm; font-size: 10.5px; }
  .footer .thanks { font-size: 12px; font-weight: 700; margin-bottom: 1mm; }
  .footer .note { font-size: 9.5px; color: #333; }
  .qr { text-align: center; margin: 2mm 0; }
  .qr img { width: 22mm; height: 22mm; }
  .qr-caption { font-size: 9px; color: #444; }
  .barcode-num { font-family: 'Courier New', monospace; font-size: 11px; letter-spacing: 1px; text-align: center; margin: 1mm 0; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  /* On-screen actions, hidden when printing */
  .actions { padding: 4mm; text-align: center; background: #f5f5f5; }
  .actions button { background: #111; color: #fff; border: 0; padding: 8px 18px; border-radius: 4px; cursor: pointer; font-size: 12px; }
  @media print { .actions { display: none; } }
</style>
</head>
<body>
  <div class="receipt">
    <div class="center">
      ${tpl.show_logo && tpl.logo_url ? `<img class="biz-logo" src="${escape(resolveImg(tpl.logo_url))}" alt="" />` : ""}
      <div class="biz-name">${escape(isAR ? tpl.business_name_ar : tpl.business_name_en) || ""}</div>
      <div class="biz-meta">
        ${(isAR ? tpl.address_ar : tpl.address_en) ? `<div>${escape(isAR ? tpl.address_ar : tpl.address_en)}</div>` : ""}
        ${bizPhone ? `<div dir="ltr">${t("Tel", "هاتف")}: ${escape(bizPhone)}</div>` : ""}
        ${tpl.tax_number ? `<div>${t("VAT", "ر.ضريبي")}: ${escape(tpl.tax_number)}</div>` : ""}
        ${tpl.cr_number ? `<div>${t("CR", "س.ت")}: ${escape(tpl.cr_number)}</div>` : ""}
      </div>
      <div class="doc-title">${escape(isAR ? tpl.header_title_ar : tpl.header_title_en) || (isAR ? "إيصال" : "Receipt")}</div>
    </div>

    <div class="divider"></div>

    <div class="info-line"><span class="lbl">${t("Receipt #", "رقم الإيصال")}</span><span class="val">${escape(invoice.invoice_number || "")}</span></div>
    <div class="info-line"><span class="lbl">${t("Date", "التاريخ")}</span><span class="val">${dateStr}</span></div>
    ${tpl.show_customer && invoice.customer_name && invoice.customer_name !== "Walk-in" ? `<div class="info-line"><span class="lbl">${t("Customer", "العميل")}</span><span class="val">${escape(invoice.customer_name)}</span></div>` : ""}
    ${tpl.show_phone && invoice.customer_phone ? `<div class="info-line"><span class="lbl">${t("Phone", "الهاتف")}</span><span class="val">${escape(invoice.customer_phone)}</span></div>` : ""}
    <div class="info-line"><span class="lbl">${t("Payment", "الدفع")}</span><span class="val">${escape(formatPaymentLine(invoice, isAR))}</span></div>

    <div class="divider"></div>

    <table>
      <thead>
        <tr>
          <th class="item-h">${t("Item", "البند")}</th>
          <th class="qty">${t("Qty", "كم")}</th>
          <th class="amt">${t("Amt", "المبلغ")}</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>

    <div class="divider"></div>

    <div class="totals">
      <div class="info-line"><span class="lbl">${t("Subtotal", "المجموع")}</span><span class="val">${fmt(invoice.subtotal)}</span></div>
      ${tpl.show_discount && invoice.discount ? `<div class="info-line"><span class="lbl">${t("Discount", "خصم")}</span><span class="val">-${fmt(invoice.discount)}</span></div>` : ""}
      ${invoice.coupon_discount ? `<div class="info-line"><span class="lbl">${t("Coupon", "كوبون")} ${invoice.coupon_code ? `(${escape(invoice.coupon_code)})` : ""}</span><span class="val">-${fmt(invoice.coupon_discount)}</span></div>` : ""}
      ${invoice.loyalty_value_applied ? `<div class="info-line"><span class="lbl">${t("Points", "نقاط")}</span><span class="val">-${fmt(invoice.loyalty_value_applied)}</span></div>` : ""}
      ${tpl.show_tax && invoice.tax ? `<div class="info-line"><span class="lbl">${t("VAT", "ضريبة")}</span><span class="val">${fmt(invoice.tax)}</span></div>` : ""}
      <div class="info-line grand"><span>${t("TOTAL", "الإجمالي")}</span><span>${fmt(invoice.total)} ${isAR ? "ر.ق" : "QAR"}</span></div>
    </div>

    ${tpl.show_loyalty && invoice.loyalty_earned ? `
      <div class="info-line" style="margin-top:1.5mm;"><span class="lbl">${t("Loyalty earned", "نقاط مكتسبة")}</span><span class="val">+${invoice.loyalty_earned}</span></div>
    ` : ""}

    ${qrUrl ? `
      <div class="double-divider"></div>
      <div class="qr">
        <img src="${qrUrl}" alt="qr" />
        <div class="qr-caption">${t("Scan for e-receipt", "امسح للإيصال الإلكتروني")}</div>
      </div>
    ` : ""}

    <div class="divider"></div>

    <div class="footer">
      <div class="thanks">${escape(isAR ? tpl.footer_text_ar : tpl.footer_text_en) || ""}</div>
      ${(isAR ? tpl.footer_note_ar : tpl.footer_note_en) ? `<div class="note">${escape(isAR ? tpl.footer_note_ar : tpl.footer_note_en)}</div>` : ""}
    </div>
  </div>

  <div class="actions">
    <button onclick="window.print()">${t("Print", "طباعة")}</button>
  </div>

  <script>setTimeout(function(){ window.print(); }, 300);</script>
</body>
</html>
  `;

  const w = window.open("", "_blank", `width=${widthMM * 4 + 80},height=900`);
  if (!w) return;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  w.location.href = url;
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch {} }, 60_000);
}
